import React, {useEffect, useState} from "react";
import {
  Board,
  BoardHeader,
  BoardSearchResult,
  CancelButton,
  Input,
  SubmitContainer
} from "../../assets/GlobalStyles";
import {useAtom} from "jotai";
import {toast} from "react-toastify";
import {multiAxiosCall} from "../../common/StringUtils";
import {useLocation, useNavigate} from "react-router-dom";
import {
  budgetTimes,
  budgetTimesDirect,
  timeBudgetDetailDataAtom
} from "./entity/BudgetTime";
import DragToSelect from "../../components/common/DragToSelect";
import InsertToSelect from "../../components/common/InsertToSelect";
import {
  resistBudgetTimes,
  selBudgetTimeDetailInfo,
  updateBudgetTimes
} from "../../services/settings/BudgetTimeAxios";
import {ValidationScript} from "../signup/styles";
import {Controller, useForm} from "react-hook-form";
import styled from "styled-components";

const EXPOSURE_TIME_TYPES = {
  EQUAL_DISTRIBUTION: {
    title: "균등 소진",
    description: "선택한 시간대에 예산을 균등하게 분배합니다."
  },
  FAST_EXHAUSTION: {
    title: "빠른 소진",
    description: "선택한 시간대에 예산을 우선적으로 소진합니다."
  },
  DIRECT_SETTINGS: {
    title: "직접 설정",
    description: "요일과 시간별 예산 비율을 직접 입력합니다."
  }
};

const cloneTimeData = source =>
    (source ?? []).map(row => [...row]);

function BudgetTimeDetail() {
  const [timeBudgetDetailDataState, setTimeBudgetDetailDataState] =
      useAtom(timeBudgetDetailDataAtom);

  const navigate = useNavigate();
  const {state} = useLocation();
  const [isSaving, setIsSaving] = useState(false);
  const [saveType, setSaveType] = useState("update");
  const [checkIndex, setCheckIndex] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: {errors},
    setValue
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      groupName: ""
    }
  });

  const userId = state?.id ?? timeBudgetDetailDataState?.userId;
  const groupId = state?.groupId;
  const isCreateMode = saveType === "resist";
  const exposureTimeType = timeBudgetDetailDataState?.exposureTimeType;

  const onError = error => {
    console.error("시간별 예산 그룹 입력값 확인:", error);
  };

  useEffect(() => {
    const initialize = async () => {
      if (!userId) {
        toast.error("광고주 정보를 확인할 수 없습니다.", {
          toastId: "budgetTimeUserNotFound"
        });
        return;
      }

      try {
        if (groupId !== undefined && groupId !== null) {
          const response = await selBudgetTimeDetailInfo(userId, groupId);

          if (response) {
            setTimeBudgetDetailDataState(response);
            reset(response);
          }

          setSaveType("update");
          return;
        }

        const initialData = {
          allowTimes: cloneTimeData(budgetTimes),
          exposureTimeType: "EQUAL_DISTRIBUTION",
          groupName: `시간별 예산 그룹명${Number(state?.listCount ?? 0) + 1}`,
          userId
        };

        setTimeBudgetDetailDataState(initialData);
        reset(initialData);
        setSaveType("resist");
      } catch (error) {
        console.error("시간별 예산 그룹 조회 실패:", error);
        toast.error("시간별 예산 그룹 정보를 불러오는 중 오류가 발생했습니다.", {
          toastId: "budgetTimeDetailLoadError"
        });
      }
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRadioSelect = event => {
    const selectedType = event.target.value;

    setTimeBudgetDetailDataState(prev => ({
      ...prev,
      exposureTimeType: selectedType,
      allowTimes:
          selectedType === "DIRECT_SETTINGS"
              ? cloneTimeData(budgetTimesDirect)
              : cloneTimeData(budgetTimes)
    }));

    setCheckIndex(null);
  };

  const handleGroupName = event => {
    const {value} = event.target;

    setTimeBudgetDetailDataState(prev => ({
      ...prev,
      groupName: value
    }));

    setValue("groupName", value, {
      shouldDirty: true,
      shouldValidate: true
    });
  };

  const validateTimeSettings = () => {
    const allowTimes = timeBudgetDetailDataState?.allowTimes ?? [];
    setCheckIndex(null);

    if (exposureTimeType === "DIRECT_SETTINGS") {
      let invalidWeekIndex = null;

      const weekSumList = allowTimes.map((rowData, index) => {
        const rowSum = (rowData ?? []).reduce((sum, value) => {
          const numberValue = Number.parseInt(value, 10);
          return sum + (Number.isNaN(numberValue) ? 0 : numberValue);
        }, 0);

        if (
            invalidWeekIndex === null &&
            rowSum > 0 &&
            rowSum !== 100
        ) {
          invalidWeekIndex = index;
        }

        return rowSum;
      });

      if (invalidWeekIndex !== null) {
        setCheckIndex(invalidWeekIndex);
        toast.warning("해당 요일의 시간별 예산 합계를 100%로 설정해주세요.");
        return false;
      }

      const totalSum = weekSumList.reduce((sum, value) => sum + value, 0);

      if (totalSum === 0) {
        toast.warning("요일 및 시간별 예산 값을 입력해주세요.");
        return false;
      }

      return true;
    }

    const hasSelectedTime = allowTimes.some(rowData =>
        (rowData ?? []).some(value => value === true)
    );

    if (!hasSelectedTime) {
      toast.warning("광고 노출 요일 및 시간을 선택해주세요.");
      return false;
    }

    return true;
  };

  const onSubmit = async () => {
    if (isSaving) {
      return;
    }

    if (!validateTimeSettings()) {
      return;
    }

    setIsSaving(true);

    try {
      const response = isCreateMode
          ? await resistBudgetTimes(
              timeBudgetDetailDataState
          )
          : await updateBudgetTimes(
              timeBudgetDetailDataState
          );

      if (!response) {
        throw new Error(
            isCreateMode
                ? "시간별 예산 그룹 저장 실패"
                : "시간별 예산 그룹 수정 실패"
        );
      }

      toast.success(
          isCreateMode
              ? "저장되었습니다."
              : "수정되었습니다.",
          {
            toastId: "saveBudgetTimes",
            autoClose: 1000
          }
      );

      /*
       * 성공 토스트가 화면에 표시된 후 목록으로 이동한다.
       * toast의 onClose에는 화면 이동을 연결하지 않는다.
       */
      window.setTimeout(() => {
        navigate(
            "/board/budgetTimeList",
            {
              state: {
                id:
                timeBudgetDetailDataState.userId
              }
            }
        );
      }, 700);
    } catch (error) {
      console.error(
          "시간별 예산 그룹 저장 실패:",
          error
      );

      toast.error(
          isCreateMode
              ? "저장 중 오류가 발생했습니다."
              : "수정 중 오류가 발생했습니다.",
          {
            toastId: "saveBudgetTimesError"
          }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleBackToList = () => {
    navigate("/board/budgetTimeList", {
      state: {
        id: userId
      }
    });
  };

  return (
      <DetailPage>
        <form onSubmit={handleSubmit(onSubmit, onError)}>
          <BoardHeader>시간별 예산 그룹 설정</BoardHeader>

          <BoardSearchResult>
            <SettingCard>
              <SectionHeader>
                <SectionTitle>기본 정보</SectionTitle>
              </SectionHeader>

              <InnerBox>
                <FieldRow>
                  <FieldLabel>시간별 예산 그룹명</FieldLabel>

                  <FieldContent>
                    <Controller
                        name="groupName"
                        control={control}
                        rules={{
                          required: "그룹명을 입력해주세요.",
                          validate: value =>
                              Boolean(value?.trim()) || "공백만 입력할 수 없습니다."
                        }}
                        render={({field}) => (
                            <GroupNameInput
                                {...field}
                                type="text"
                                maxLength={50}
                                placeholder="그룹명을 입력해주세요."
                                value={timeBudgetDetailDataState?.groupName ?? ""}
                                onChange={handleGroupName}
                            />
                        )}
                    />

                    {errors.groupName && (
                        <ValidationScript style={{position: "unset"}}>
                          {errors.groupName.message}
                        </ValidationScript>
                    )}
                  </FieldContent>
                </FieldRow>

                <FieldRow>
                  <FieldLabel>예산 소진 설정</FieldLabel>

                  <FieldContent>
                    <ExhaustOptionGroup>
                      {Object.entries(EXPOSURE_TIME_TYPES).map(
                          ([value, option]) => {
                            const active = exposureTimeType === value;

                            return (
                                <ExhaustOption key={value} $active={active}>
                                  <input
                                      type="radio"
                                      name="exhaust"
                                      value={value}
                                      checked={active}
                                      onChange={handleRadioSelect}
                                  />

                                  <OptionRadio $active={active}/>

                                  <OptionText>
                                    <OptionTitle>{option.title}</OptionTitle>
                                    <OptionDescription>
                                      {option.description}
                                    </OptionDescription>
                                  </OptionText>
                                </ExhaustOption>
                            );
                          }
                      )}
                    </ExhaustOptionGroup>
                  </FieldContent>
                </FieldRow>
              </InnerBox>
            </SettingCard>

            <SettingCard>
              <SectionHeader>
                <SectionTitle>
                  {exposureTimeType === "DIRECT_SETTINGS"
                      ? "요일 및 시간별 예산 설정"
                      : "광고 노출 요일 및 시간 설정"}
                </SectionTitle>

                <SectionGuide>
                  {exposureTimeType === "DIRECT_SETTINGS"
                      ? "요일별 시간 예산의 합계가 100%가 되도록 입력해주세요."
                      : "드래그하여 광고를 노출할 요일과 시간을 선택해주세요."}
                </SectionGuide>
              </SectionHeader>

              <ScheduleInnerBox>
                <ScheduleEditorHeader>
                  <EditorGuide>
                    {exposureTimeType === "DIRECT_SETTINGS"
                        ? "각 시간대에 적용할 예산 비율을 % 단위로 설정합니다."
                        : "선택한 영역은 노출, 선택하지 않은 영역은 미노출로 적용됩니다."}
                  </EditorGuide>

                  {exposureTimeType !== "DIRECT_SETTINGS" && (
                      <SelectionLegend>
                        <SelectShape $active={true}>
                          <span>노출</span>
                        </SelectShape>
                        <SelectShape>
                          <span>미노출</span>
                        </SelectShape>
                      </SelectionLegend>
                  )}
                </ScheduleEditorHeader>

                <EditorContent>
                  {["EQUAL_DISTRIBUTION", "FAST_EXHAUSTION"].includes(
                      exposureTimeType
                  ) && <DragToSelect reset={reset}/>}

                  {exposureTimeType === "DIRECT_SETTINGS" && (
                      <InsertToSelect checkWeek={checkIndex}/>
                  )}
                </EditorContent>
              </ScheduleInnerBox>
            </SettingCard>
          </BoardSearchResult>

          <BottomActionBar>
            <BottomActionGuide>
              <BottomActionTitle>시간별 예산 그룹 설정</BottomActionTitle>
              <BottomActionDescription>
                설정 내용을 확인한 뒤 저장하거나 목록으로 이동할 수 있습니다.
              </BottomActionDescription>
            </BottomActionGuide>
            <BottomActionButtons>
              <CancelButton type="button" onClick={handleBackToList}>
                목록
              </CancelButton>

              <SaveButton
                  type="submit"
                  disabled={isSaving}
              >
                {isSaving
                    ? "처리 중"
                    : isCreateMode
                        ? "저장"
                        : "수정"}
              </SaveButton>
            </BottomActionButtons>
          </BottomActionBar>
        </form>
      </DetailPage>
  );
}

export default BudgetTimeDetail;

const DetailPage = styled(Board)`
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  padding: 0 20px 20px;
  border: 0;
  border-radius: 0;
  box-shadow: none;
`;

const BottomActionBar = styled(SubmitContainer)`
  position: sticky;
  bottom: 12px;
  z-index: 20;
  width: 100%;
  box-sizing: border-box;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 22px;
  padding: 16px 20px;
  border: 1px solid #ffd7b5;
  border-left: 4px solid #f5811f;
  border-radius: 10px;
  background: linear-gradient(90deg, #fff8f2 0%, rgba(255, 255, 255, 0.98) 48%);
  box-shadow: 0 -4px 20px rgba(245, 129, 31, 0.09);

  button {
    width: auto;
    min-width: 108px;
    height: 40px;
    margin: 0;
    padding: 0 22px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 700;
  }

  @media (max-width: 640px) {
    align-items: stretch;
    flex-direction: column;

    button {
      width: 100%;
    }
  }
`;

const BottomActionGuide = styled.div`
  min-width: 0;
  text-align: left;
`;

const BottomActionTitle = styled.strong`
  display: block;
  color: #2f3438;
  font-size: 14px;
  font-weight: 800;
`;

const BottomActionDescription = styled.span`
  display: block;
  margin-top: 4px;
  color: #7f878e;
  font-size: 12px;
  line-height: 1.45;
`;

const BottomActionButtons = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;

  @media (max-width: 640px) {
    width: 100%;
    flex-direction: column;
  }
`;

const SettingCard = styled.div`
  margin-bottom: 20px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 14px;
`;

const SectionTitle = styled.div`
  color: #222;
  font-size: 17px;
  font-weight: 700;
`;

const SectionGuide = styled.span`
  color: #888;
  font-size: 14px;
  line-height: 1.5;
  text-align: right;
`;

const InnerBox = styled.div`
  padding: 22px 20px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background-color: #f9fafb;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 14px 0;
  border-bottom: 1px solid #f0f0f0;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const FieldLabel = styled.div`
  width: 140px;
  min-width: 140px;
  padding-top: 10px;
  color: #444;
  font-size: 16px;
  font-weight: 600;

  @media (max-width: 900px) {
    width: auto;
    min-width: 0;
    padding-top: 0;
  }
`;

const FieldContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`;

const GroupNameInput = styled(Input)`
  width: 100%;
  min-width: 340px;
  max-width: 460px;
  height: 40px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background-color: #fff;

  &:focus {
    border-color: #f5811f;
  }

  &::placeholder {
    color: #aaa;
  }

  @media (max-width: 600px) {
    min-width: 0;
  }
`;

const ExhaustOptionGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
  max-width: 920px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ExhaustOption = styled.label`
  position: relative;
  display: flex;
  min-height: 72px;
  align-items: flex-start;
  gap: 11px;
  padding: 14px 15px;
  border: 1px solid ${({$active}) => ($active ? "#f5811f" : "#e5e5e5")};
  border-radius: 8px;
  background-color: ${({$active}) => ($active ? "#fff8f1" : "#fff")};
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:hover {
    border-color: #f5811f;
  }

  input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }
`;

const OptionRadio = styled.span`
  position: relative;
  width: 17px;
  height: 17px;
  flex: 0 0 17px;
  margin-top: 1px;
  border: 1px solid ${({$active}) => ($active ? "#f5811f" : "#cfcfcf")};
  border-radius: 50%;
  background-color: #fff;

  &::after {
    position: absolute;
    top: 3px;
    left: 3px;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background-color: ${({$active}) => ($active ? "#f5811f" : "transparent")};
    content: "";
  }
`;

const OptionText = styled.span`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 5px;
`;

const OptionTitle = styled.span`
  color: #333;
  font-size: 15px;
  font-weight: 700;
`;

const OptionDescription = styled.span`
  color: #888;
  font-size: 13px;
  line-height: 1.4;
`;

const ScheduleInnerBox = styled.div`
  padding: 20px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background-color: #f9fafb;
`;

const ScheduleEditorHeader = styled.div`
  display: flex;
  min-height: 30px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
`;

const EditorGuide = styled.span`
  color: #888;
  font-size: 14px;
`;

const SelectionLegend = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const EditorContent = styled.div`
  overflow-x: auto;
  padding: 16px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background-color: #fff;

  scrollbar-width: thin;
  scrollbar-color: #c7c7c7 transparent;

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background-color: #c7c7c7;
  }
`;

const SelectShape = styled.div`
  display: inline-flex;
  height: 26px;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  border: 1px solid ${({$active}) => ($active ? "#f5811f" : "#e5e5e5")};
  border-radius: 13px;
  background-color: ${({$active}) => ($active ? "#fff4e8" : "#fff")};
  color: ${({$active}) => ($active ? "#f5811f" : "#777")};
  font-size: 13px;
  font-weight: 600;
`;

const SaveButton = styled.button`
  min-width: 84px;
  height: 38px;
  padding: 0 18px;
  border: 1px solid #f5811f;
  border-radius: 6px;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: #ffe8d2;
  }

  &:disabled {
    border-color: #ddd;
    background-color: #f5f5f5;
    color: #aaa;
    cursor: not-allowed;
  }
`;
